import React, { useEffect, useState } from "react";

import ConfirmationModal from "./ConfirmationModal";

type ConfirmColor = "error" | "warning" | "primary" | "secondary" | "success";

interface ModalState {
    open: boolean;
    title: string;
    message: string;
    confirmColor: ConfirmColor;
    confirmText: string;
    timer?: number;
}

const DEFAULT_STATE: ModalState = {
    open: false,
    title: "",
    message: "",
    confirmColor: "primary",
    confirmText: "OK",
};

let _show: ((state: ModalState) => void) | null = null;

const iconToColor = (icon?: string): ConfirmColor => {
    switch (icon) {
        case "error":
            return "error";
        case "success":
            return "success";
        case "warning":
            return "warning";
        default:
            return "primary";
    }
};

const hasText = (v?: unknown): v is string =>
    typeof v === "string" && v.trim().length > 0;

const DEFAULTS: Record<string, { title: string; message: string }> = {
    error: {
        title: "Something went wrong",
        message: "An unexpected error occurred. Please try again.",
    },
    success: {
        title: "Success",
        message: "Operation completed successfully.",
    },
    warning: {
        title: "Warning",
        message: "Please review the highlighted items and try again.",
    },
    info: { title: "Notice", message: "Operation completed." },
};

const defaultsFor = (icon?: string) => DEFAULTS[icon ?? ""] ?? DEFAULTS.info;

export const emitConfirmationModal = (props: {
    title?: string;
    message?: string;
    icon?: string;
    timer?: number;
}) => {
    if (_show) {
        const fallback = defaultsFor(props.icon);
        _show({
            open: true,
            title: hasText(props.title) ? props.title : fallback.title,
            message: hasText(props.message) ? props.message : fallback.message,
            confirmColor: iconToColor(props.icon),
            confirmText: "OK",
            timer: props.timer,
        });
    }
};

const ConfirmationModalRoot: React.FC = () => {
    const [state, setState] = useState<ModalState>(DEFAULT_STATE);

    useEffect(() => {
        _show = setState;
        return () => {
            _show = null;
        };
    }, []);

    useEffect(() => {
        if (state.open && state.timer) {
            const id = setTimeout(() => {
                setState((prev) => ({ ...prev, open: false }));
            }, state.timer);
            return () => clearTimeout(id);
        }
    }, [state.open, state.timer]);

    const handleClose = () => setState((prev) => ({ ...prev, open: false }));

    return (
        <ConfirmationModal
            open={state.open}
            onClose={handleClose}
            onConfirm={handleClose}
            title={state.title}
            message={state.message}
            confirmColor={state.confirmColor}
            confirmText={state.confirmText}
            hideCancelButton
        />
    );
};

export default ConfirmationModalRoot;
